/**
 * Partners Section with logo strip and hover animations
 */
import { motion } from 'motion/react';
import { Award } from 'lucide-react';
import { content } from '@/landing/data/content';

export function Partners() {
  return (
    <section id="partners" className="relative py-24 lg:py-32 bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)] overflow-hidden">
      <div className="absolute inset-0 hero-grid-overlay opacity-40" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-4">
            Our Partners
          </h2>
          <p className="text-xl text-[var(--text-muted)] max-w-3xl mx-auto font-light">
            Trusted by leading institutions across the region
          </p>
        </motion.div>

        {/* Partner Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch">
          {content.partners.map((partner, index) => (
            <motion.div
              key={partner.name}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group relative flex flex-col rounded-2xl overflow-visible border border-[rgba(201,168,76,0.2)] hover:border-[rgba(201,168,76,0.35)] transition-all duration-500 h-full"
            >
              {/* Tier Badge — hovering over card corner (overflow-visible so not clipped) */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 + 0.3, type: 'spring', stiffness: 200 }}
                className="absolute -top-4 -right-4 z-10"
              >
                <div className={`px-4 py-2 rounded-full shadow-lg flex items-center gap-2 ${
                  partner.tier === 'Premium'
                    ? 'bg-[var(--gold)] text-[var(--navy)]'
                    : 'bg-[var(--royal)] text-white'
                } font-semibold text-sm`}>
                  <Award className="w-4 h-4" />
                  {partner.tier}
                </div>
              </motion.div>

              <div className="pt-8 pb-2 flex-shrink-0 overflow-hidden rounded-t-2xl">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  className="relative w-32 h-32 mx-auto rounded-2xl bg-[rgba(26,86,219,0.2)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center overflow-hidden"
                >
                <img
                  src="https://images.unsplash.com/photo-1637433496898-763a0a9ed579?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwbG9nbyUyMGJ1aWxkaW5nfGVufDF8fHx8MTc3MTQ5ODAxOHww&ixlib=rb-4.1.0&q=80&w=1080"
                  alt={partner.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                </motion.div>
              </div>

              {/* Content (writing half — gradient, fills remaining space) */}
              <div className="insolip-card-gradient flex-1 flex flex-col justify-center rounded-b-2xl p-8 pt-0 min-h-[140px]">
                <h3 className="font-display text-2xl font-semibold text-white mb-3 text-center group-hover:text-[var(--gold)] transition-colors duration-300">
                  {partner.name}
                </h3>
                <p className="text-white/85 text-center leading-relaxed font-light">
                  {partner.description}
                </p>
              </div>

            </motion.div>
          ))}
        </div>

        {/* Become a Partner CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center mt-16"
        >
          <div className="inline-block max-w-2xl p-8 rounded-3xl border border-[rgba(201,168,76,0.25)] insolip-card-gradient">
            <h3 className="font-display text-3xl font-semibold text-white mb-3">
              Become a Partner
            </h3>
            <p className="text-white/80 mb-6 leading-relaxed font-light">
              Join leading institutions using INSOLIP to transform their innovation management processes
            </p>
            <a
              href="#contact"
              className="inline-block px-8 py-4 bg-[var(--gold)] text-[var(--navy)] font-semibold rounded-full hover:opacity-90 hover:scale-105 transition-all duration-300"
            >
              Partner With Us
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
